import { Injectable, isDevMode } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { UserDaoService } from 'src/app/dao/user-dao-service/user-dao.service';
import { UserStorageService } from 'src/app/services/user-storage-service/user-storage.service';
import { User } from 'src/app/models/user-model/user.model';
import { RolBasic } from 'src/app/models/roles-models/rol-basic.model';
import { Center } from 'src/app/models/center-model/center.model';
import { Rol } from 'src/app/models/roles-models/rol.model';
import { FormGroup } from '@angular/forms';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';
import { LoginVerificationContact } from 'src/app/models/login-verification-model/login-verification-contact.model';

@Injectable()
export class UserService {

  accessToken = 'USE';
  refreshUsersListener = new Subject();
  refreshUserListener = new Subject();
  searchUsersListener = new Subject();
  toggleUserDispositionListener = new Subject<any>();
  rememberedPermissions = new Array<RolBasic>();
  rememberedUserImage: string;
  rememberedUserForm: FormGroup;

  constructor(private router: Router,
              private userDaoService: UserDaoService,
              private userStorageService: UserStorageService) { }

  public getUsers() {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.getUsers(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const users = new Array<User>();
          o['data']['userList'].forEach(
            (user) => {
              const u = new User();
              Object.assign(u, user);
              const roles = new Array<RolBasic>();
              user['permissionList'].forEach(
                (rol) => {
                  roles.push(Object.assign(new RolBasic(), rol));
                }
              );
              u.PermissionList = roles;
              u.LoginVerificationContacts = this.mapLoginVerificationContacts(user['loginVerificationContacts']);
              users.push(u);
            }
          );
          return users;
        })
      );
    }
    return EMPTY;
  }

  public getUserInfo(internalId) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.getUserInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const user = new User();
          Object.assign(user, o['data']['userDetails']);
          const permissionList = new Array<RolBasic>();
          o['data']['userDetails']['permissionList'].forEach(
            (p) => {
              const rol = new RolBasic();
              Object.assign(rol, p);
              permissionList.push(rol);
            }
          );
          user.PermissionList = permissionList;
          user.LoginVerificationContacts = this.mapLoginVerificationContacts(o['data']['userDetails']['loginVerificationContacts']);
          return user;
        })
      );
    }
    return EMPTY;
  }

  public addNewUser(companyInternalId, firstname, lastname, username, password, email, userImageRaw, permissionList) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.addNewUser(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), companyInternalId, firstname, lastname, username, password, email, userImageRaw, permissionList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateUser(internalId, companyInternalId, firstname, lastname, username, email) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.updateUser(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, companyInternalId, firstname, lastname, username, email).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteUser(internalId) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.deleteUser(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateUserImage(internalId, imageRaw) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.updateUserImage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, imageRaw).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateUserPermissions(internalId, companyInternalId, permissionList) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.updateUserPermissions(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, companyInternalId, permissionList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getUserCentersAndRoles() {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.getUserCentersAndRoles(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const centers = new Array<Center>();
          o['data']['centersAndRoles']['centerList'].forEach(
            (c) => {
              const center = new Center();
              Object.assign(center, c);
              centers.push(center);
            }
          );
          const roles = new Array<Rol>();
          o['data']['centersAndRoles']['rolesList'].forEach(
            (r) => {
              const role = new Rol();
              Object.assign(role, r);
              roles.push(role);
            }
          );
          return { centers, roles };
        })
      );
    }
    return EMPTY;
  }

  public updateUserPassword(internalId, newField) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.updateUserPassword(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId,  newField).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addLoginVerificationContact(userInternalId, type, value) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.addLoginVerificationContact(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), userInternalId, type, value).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteLoginVerificationContact(internalId) {
    if (this.accessGranted(this.accessToken)) {
      return this.userDaoService.deleteLoginVerificationContact(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public signIn(lang, mail, udid, hashedPassword) {
    return this.userDaoService.signIn(lang, mail, udid, hashedPassword).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public webLogin(lang, mail, udid, hashedPassword) {
    return this.userDaoService.webLogin(lang, mail, udid, hashedPassword).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public sendLoginVerificationCode(lang, udid, challengeId, contactInternalId) {
    return this.userDaoService.sendLoginVerificationCode(lang, udid, challengeId, contactInternalId).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public verifyLoginVerificationCode(lang, udid, challengeId, code) {
    return this.userDaoService.verifyLoginVerificationCode(lang, udid, challengeId, code).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public ssoMicrosoftLogin(lang, udid, token) {
    return this.userDaoService.ssoMicrosoftLogin(lang, udid, token).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public ssoGoogleLogin(lang, udid, token) {
    return this.userDaoService.ssoGoogleLogin(lang, udid, token).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public selectCompany(tmpContext, lang, udid, companyInternalId) {
    return this.userDaoService.selectCompany(tmpContext, lang, udid, companyInternalId).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public recoveryPass(lang, udid, mail) {
    return this.userDaoService.recoveryPass(lang, udid, mail).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data']['success'])
    );
  }

  public verifyPass(lang, udid, recoveryToken, newPassword) {
    return this.userDaoService.verifyPass(lang, udid, recoveryToken, newPassword).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public signOut() {
    this.router.navigate(['/signin']);
    this.userStorageService.deleteAllItems();
  }

  public accessGranted(accessToken) {
    if (Array.isArray(accessToken)) {
      for (const menuSection of this.userStorageService.getMenuSectionList()) {
        if (accessToken.find(at => at === menuSection.OptionId)) {
          return true;
        }
      }
    } else {
      for (const menuSection of this.userStorageService.getMenuSectionList()) {
        if (accessToken === menuSection.OptionId) {
          return true;
        }
      }
    }
    return false;
  }

  public rememberUserInfo(rememberedUserImage, userForm) {
    this.rememberedUserImage = rememberedUserImage;
    this.rememberedUserForm = userForm;
  }

  public getRememberedUserImage() {
    return this.rememberedUserImage;
  }

  public getRememberedUserForm() {
    return this.rememberedUserForm;
  }

  public getRememberedPermissions() {
    return this.rememberedPermissions;
  }

  public rememberPermission(permission) {
    this.rememberedPermissions.push(permission);
  }

  public deleteRememberedPermission(permission) {
    for (const p of this.rememberedPermissions) {
      if ((p.CenterName === permission.CenterName) && (p.RoleName === permission.RoleName)) {
        this.rememberedPermissions.splice(this.rememberedPermissions.indexOf(p), 1);
      }
    }
  }

  public deleteAllRememberedPermissions() {
    this.rememberedPermissions = new Array<RolBasic>();
  }

  private mapLoginVerificationContacts(rawContacts) {
    const contacts = new Array<LoginVerificationContact>();
    if (rawContacts) {
      rawContacts.forEach(
        (contact) => {
          contacts.push(Object.assign(new LoginVerificationContact(), contact));
        }
      );
    }
    return contacts;
  }

  public refreshUsersListen() {
    return this.refreshUsersListener.asObservable();
  }

  public refreshUsers() {
    this.refreshUsersListener.next();
  }
  public refreshUserListen() {
    return this.refreshUserListener.asObservable();
  }

  public refreshUser() {
    this.refreshUserListener.next();
  }

  public searchUsers(filterKey) {
    this.searchUsersListener.next(filterKey);
  }

  public searchUsersListen() {
    return this.searchUsersListener.asObservable();
  }
  public toggleUserDisposition(viewDis) {
    this.toggleUserDispositionListener.next(viewDis);
  }

  public toggleUserDispositionListen() {
    return this.toggleUserDispositionListener.asObservable();
  }
}
